import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FalkeTekLogo } from "../components/Brand";
import { formatApiErrorDetail } from "../lib/api";
import { Eye, EyeOff } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    vorname: "",
    nachname: "",
    email: "",
    password: "",
    telefon: "",
    rolle: "kunde",
    anlagenadresse: "",
    installation_datum_ca: "",
    rechnungsnummer_optional: "",
    will_gewerblich: false,
    firmenname: "",
    firmenadresse: "",
    steuernummer: "",
    ust_id_nr: "",
    kleinunternehmer: false,
    iban: "",
    bic: "",
    kontoinhaber: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register(form);
      // Bei aktivem User direkt einloggen + navigieren.
      // Bei pending-Kunden (aktiv=false) bleiben wir auf einer Erfolgsseite mit Hinweis.
      if (u && u.aktiv) {
        setSuccess("Registrierung erfolgreich!");
        setTimeout(() => navigate(u.role === "vertriebler" ? "/vertriebler" : "/app"), 800);
      } else {
        setSuccess("Registrierung erfolgreich! Dein Account wartet auf Admin-Freigabe — du erhältst eine Benachrichtigung, sobald er aktiviert wurde.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Registrierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-falke-warmwhite">
      <header className="px-6 py-5 bg-white border-b border-falke-blue/10">
        <FalkeTekLogo />
      </header>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={onSubmit} className="falke-card p-8" data-testid="register-form">
          <h2 className="text-3xl font-heading font-bold text-falke-blue mb-2">Account anlegen</h2>
          <p className="text-sm text-falke-anthracite/70 mb-8">Als Bestandskunde von FalkeTek (Solar / PV / Wärmepumpe) kannst du hier deinen Zugang anfragen.</p>

          <div className="grid sm:grid-cols-2 gap-4 mb-2">
            <Field label="Vorname *" id="reg-vorname"><input required value={form.vorname} onChange={update("vorname")} className="reg-input" data-testid="reg-vorname" /></Field>
            <Field label="Nachname *" id="reg-nachname"><input required value={form.nachname} onChange={update("nachname")} className="reg-input" data-testid="reg-nachname" /></Field>
            <Field label="Telefon" id="reg-tel"><input value={form.telefon} onChange={update("telefon")} className="reg-input" data-testid="reg-tel" /></Field>
            <Field label="E-Mail *" id="reg-email"><input required type="email" value={form.email} onChange={update("email")} className="reg-input" data-testid="reg-email" /></Field>
            <Field label="Passwort *" id="reg-password" className="sm:col-span-2">
              <div className="relative">
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  minLength={6}
                  value={form.password}
                  onChange={update("password")}
                  className="reg-input pr-12"
                  data-testid="reg-password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-falke-blue/60 hover:text-falke-orange transition p-1"
                  aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  data-testid="reg-password-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </Field>
          </div>

          <h3 className="font-heading text-lg text-falke-blue mt-6 mb-3">Deine Anlage</h3>
          <Field label="Anlagenadresse *" id="reg-addr"><input required value={form.anlagenadresse} onChange={update("anlagenadresse")} className="reg-input" data-testid="reg-addr" /></Field>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <Field label="Installations-Monat (ca.) *" id="reg-inst"><input required placeholder="z.B. 2024-05" value={form.installation_datum_ca} onChange={update("installation_datum_ca")} className="reg-input" data-testid="reg-inst" /></Field>
            <Field label="Rechnungsnr. (optional)" id="reg-rn"><input value={form.rechnungsnummer_optional} onChange={update("rechnungsnummer_optional")} className="reg-input" data-testid="reg-rn" /></Field>
          </div>

          <label className="mt-6 flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.will_gewerblich} onChange={update("will_gewerblich")} className="mt-1 w-4 h-4 accent-falke-orange" data-testid="reg-gewerblich-check" />
            <div>
              <div className="font-semibold text-falke-blue">Ich möchte gewerblicher Tippgeber werden</div>
              <div className="text-xs text-falke-anthracite/65">Du empfiehlst regelmäßig? Werde gewerblicher Tippgeber – mit Geld-Vergütung pro Abschluss statt Sachprämien.</div>
            </div>
          </label>

          {form.will_gewerblich && (
            <div className="mt-5 p-5 rounded-xl bg-falke-blue/[0.04] border border-falke-blue/10">
              <h4 className="font-heading text-falke-blue mb-3">Gewerbedaten</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Firmenname *"><input required={form.will_gewerblich} value={form.firmenname} onChange={update("firmenname")} className="reg-input" data-testid="reg-firmenname" /></Field>
                <Field label="Steuernummer *"><input required={form.will_gewerblich} value={form.steuernummer} onChange={update("steuernummer")} className="reg-input" data-testid="reg-steuernr" /></Field>
                <Field label="Firmenadresse *" className="sm:col-span-2"><input required={form.will_gewerblich} value={form.firmenadresse} onChange={update("firmenadresse")} className="reg-input" data-testid="reg-firmenaddr" /></Field>
                <Field label="USt-IdNr (optional)"><input value={form.ust_id_nr} onChange={update("ust_id_nr")} className="reg-input" data-testid="reg-ustid" /></Field>
                <label className="flex items-center gap-2 self-end pb-2">
                  <input type="checkbox" checked={form.kleinunternehmer} onChange={update("kleinunternehmer")} className="w-4 h-4 accent-falke-orange" data-testid="reg-klein" />
                  <span className="text-sm">Kleinunternehmer (§19 UStG)</span>
                </label>
                <Field label="IBAN *"><input required={form.will_gewerblich} value={form.iban} onChange={update("iban")} className="reg-input" data-testid="reg-iban" /></Field>
                <Field label="BIC *"><input required={form.will_gewerblich} value={form.bic} onChange={update("bic")} className="reg-input" data-testid="reg-bic" /></Field>
                <Field label="Kontoinhaber *" className="sm:col-span-2"><input required={form.will_gewerblich} value={form.kontoinhaber} onChange={update("kontoinhaber")} className="reg-input" data-testid="reg-inhaber" /></Field>
              </div>
            </div>
          )}

          {error && <div className="text-sm text-red-600 mt-4" data-testid="reg-error">{error}</div>}
          {success && <div className="text-sm text-falke-success mt-4" data-testid="reg-success">{success}</div>}

          <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 items-center justify-between">
            <Link to="/login" className="text-sm text-falke-blue hover:underline" data-testid="link-login">Schon einen Account? Zum Login</Link>
            <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto" data-testid="reg-submit">
              {loading ? "Erstelle..." : "Account anfordern"}
            </button>
          </div>
          <p className="text-xs text-falke-anthracite/55 mt-4">
            Dein Antrag wird durch FalkeTek geprüft. Du erhältst eine Benachrichtigung nach Freigabe.
          </p>
        </form>
      </div>
      <style>{`.reg-input { width: 100%; padding: 10px 14px; border-radius: 10px; border: 1px solid rgba(45,92,126,0.15); outline: none; }
       .reg-input:focus { border-color: #F39024; box-shadow: 0 0 0 3px rgba(243,144,36,.18); }`}</style>
    </div>
  );
}

function Field({ label, id, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-falke-blue mb-1.5" htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}
